import React, { useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

export default function Login({ onLogin }) {
  const [pat, setPat] = useState('');
  const [error, setError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pat || pat.trim().length === 0) {
      setError('Veuillez renseigner votre Personal Access Token (PAT) Azure DevOps.');
      return;
    }

    const normalizedPat = pat.trim();

    try {
      setIsValidating(true);
      azureDevOpsService.setPat(normalizedPat);

      const validation = await azureDevOpsService.validatePat(normalizedPat);
      if (!validation.valid) {
        azureDevOpsService.clearPat();
        setError('Le PAT utilisé est invalide ou a expiré. Veuillez saisir un PAT valide.');
        return;
      }

      setError(null);
      if (onLogin) onLogin();
    } catch (err) {
      console.error('Erreur lors de la configuration du PAT :', err);
      azureDevOpsService.clearPat();
      setError('Le PAT utilisé est invalide, expiré ou inaccessible.');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Connexion Azure DevOps</h2>

        <p className="text-sm text-gray-600 mb-4">
          Entrez votre Personal Access Token (PAT). Il sera utilisé pour authentifier vos requêtes vers l'API Azure DevOps.
        </p>

        {/* Information sur le stockage */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <p className="text-xs text-blue-800 font-medium mb-2">🔒 Sécurité et confidentialité</p>
          <p className="text-xs text-blue-700">
            Votre PAT est stocké localement dans le <strong>localStorage de votre navigateur</strong> (AppData). 
            Il ne sera jamais envoyé à un serveur tiers et reste uniquement sur votre machine.
          </p>
        </div>

        {/* Droits nécessaires */}
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
          <p className="text-xs text-amber-800 font-medium mb-2">⚙️ Droits requis pour le PAT</p>
          <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
            <li><strong>Work Items</strong>: Read</li>
            <li><strong>Build</strong>: Read & Execute</li>
            <li><strong>Release</strong>: Read, Write & Execute</li>
            <li><strong>Code</strong>: Read (pour les commits)</li>
            <li><strong>Test Management</strong>: Read</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700">Personal Access Token</label>
          <input
            type="password"
            className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
            value={pat}
            onChange={(e) => setPat(e.target.value)}
            placeholder="ghp_... ou Bp..."
            aria-label="Azure DevOps Personal Access Token"
          />

          {error && <div className="text-sm text-red-600 mt-2">{error}</div>}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="submit"
              disabled={isValidating}
              className={`px-4 py-2 text-white rounded ${isValidating ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600'}`}
            >
              {isValidating ? 'Vérification...' : 'Se connecter'}
            </button>
            <a className="text-sm text-blue-600 hover:underline" href="https://learn.microsoft.com/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate" target="_blank" rel="noreferrer">Comment créer un PAT ?</a>
          </div>
        </form>
      </div>
    </div>
  );
}
